import { intersection } from "lodash";
import { groupTagsByCategory } from "../components/tagSelect/tags";
import type { ECommerceSearchCriteria } from "./ECommerceSearch";
import type { ECommerceOfferProductWithNames, ECommerceOfferWithNames } from "./eCommerceTypes";
import { useEcommerceOffers } from "./useECommerceOffers";

interface ECommerceOfferToFilter {
  readonly name: ECommerceOfferWithNames["name"];
  readonly tags: ECommerceOfferWithNames["tags"];
  readonly products: Pick<ECommerceOfferProductWithNames, "name" | "providerName" | "options">[];
  readonly packageId: string | undefined;
}

export const useFilteredECommerceOffers = (searchCriteria: ECommerceSearchCriteria) => {
  const { offers, isLoading } = useEcommerceOffers({ from: searchCriteria.month.format("YYYY-MM") });
  const filteredOffers = filterOffers(offers, searchCriteria);
  return { offers: filteredOffers, isLoading };
};

function filterOffers<T extends ECommerceOfferToFilter>(
  offers: T[],
  { text, month, tags, packageOnly }: ECommerceSearchCriteria,
) {
  if (text.length === 0 && month === null && tags.length === 0) {
    return offers;
  }
  const tagsByCategory = groupTagsByCategory(tags);
  const tagsEmpty = Object.keys(tagsByCategory).length === 0;
  return offers
    .filter((o) => productNameMatches(o, text) || offerNameMatches(o, text) || providerNameMatches(o, text))
    .filter((o) => tagsEmpty || tagsMatches(o, tagsByCategory))
    .filter((o) => !packageOnly || !!o.packageId);
}

function productNameMatches({ products }: ECommerceOfferToFilter, query: string) {
  return products.some((p) => p.name?.toLowerCase().includes(query));
}

function offerNameMatches({ name }: ECommerceOfferToFilter, query: string) {
  return name?.toLowerCase().includes(query);
}

function providerNameMatches({ products }: ECommerceOfferToFilter, query: string) {
  return products.some((p) => p.providerName?.toLowerCase().includes(query));
}

function tagsMatches({ tags }: ECommerceOfferToFilter, tagsByCategory: Record<string, string[]>) {
  return Object.entries(tagsByCategory).every(([category, tagsGroup]) => {
    const tagsForCategory = tags.filter((t) => t.category === category).map((t) => t.value);
    return intersection(tagsGroup, tagsForCategory).length > 0;
  });
}
